import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowRight, 
  Calendar, 
  Clock, 
  Users, 
  Heart,
  Eye,
  Ear,
  Sparkles,
  Flower2,
  ExternalLink,
  Mail,
  Menu,
  X,
  Phone,
  Play
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_FORM_LINK = "https://forms.gle/yco5zWDZo8Hm45WTA";
const DONATE_LINK = "https://razorpay.me/@jeevandarshanfoundation";
const YOUTUBE_VIDEO = "https://www.youtube.com/embed/K7ktYRjQTFs";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.15 } }
};

// Navigation Component
export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#about", label: "About" },
    { href: "#unique", label: "What's Unique" },
    { href: "#structure", label: "Structure" },
    { href: "#facilitator", label: "Facilitator" },
    { href: "#events", label: "Events" },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-nav border-b border-subtle shadow-ambient" : "bg-transparent"
      }`}
      data-testid="navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="#" className="font-heading text-xl md:text-2xl font-semibold text-charcoal" data-testid="nav-logo">
            JDF <span className="text-sage">Life Workshop</span>
          </a>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="font-body text-sm text-muted-foreground hover:text-charcoal transition-colors"
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </a>
            ))}
            <a
              href={GOOGLE_FORM_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="font-body text-sm bg-sage text-white px-5 py-2.5 rounded-full hover:bg-sage/90 transition-all duration-300 hover:scale-105"
              data-testid="nav-register-btn"
            >
              Register
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-charcoal p-2"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden pb-6 bg-bone/95 backdrop-blur-sm"
              data-testid="mobile-menu"
            >
              <div className="flex flex-col gap-4 pt-4">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      setIsOpen(false);
                      const targetId = link.href.replace('#', '');
                      const targetElement = document.getElementById(targetId);
                      if (targetElement) {
                        setTimeout(() => {
                          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                      }
                    }}
                    className="font-body text-base text-charcoal hover:text-sage transition-colors"
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </a>
                ))}
                <a
                  href={GOOGLE_FORM_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-body text-sm bg-sage text-white px-5 py-2.5 rounded-full text-center hover:bg-sage/90 transition-all duration-300"
                  data-testid="mobile-register-btn"
                >
                  Register
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

// Hero Section
export const HeroSection = () => {
  return (
    <section className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1761468720849-5abc8533343c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxjYWxtJTIwbmF0dXJlJTIwbWVkaXRhdGlvbiUyMGFic3RyYWN0JTIwb3JnYW5pY3xlbnwwfHx8fDE3NzAzNzYwNTN8MA&ixlib=rb-4.1.0&q=85"
          alt="Calm nature"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-bone/60 via-bone/80 to-bone" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
        >
          <motion.p
            variants={fadeInUp}
            className="font-body text-sm tracking-widest text-sage uppercase mb-8"
          >
            A Space for Self-Inquiry
          </motion.p>
          
          <motion.h1
            variants={fadeInUp}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-charcoal leading-tight mb-8"
            data-testid="hero-headline"
          >
            Can life be lived <em className="italic text-sage">differently?</em>
          </motion.h1>
          
          <motion.p
            variants={fadeInUp}
            className="font-body text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-12"
          >
            A 3-Day JDF Life Workshop for deep self-inquiry, awareness, and inner transformation
          </motion.p>

          <motion.div
            variants={fadeInUp}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
          >
            <a
              href={GOOGLE_FORM_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="font-body bg-sage text-white px-8 py-4 rounded-full hover:bg-sage/90 transition-all duration-500 hover:scale-105 flex items-center gap-2 shadow-ambient"
              data-testid="hero-register-btn"
            >
              Register for the Upcoming Workshop
              <ArrowRight size={18} />
            </a>
          </motion.div>

          <motion.p
            variants={fadeInUp}
            className="font-body text-sm text-muted-foreground"
          >
            Free • Monthly • Online
          </motion.p>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-sage/40 rounded-full flex justify-center pt-2">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-1.5 h-1.5 bg-sage rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
};

// About Workshop Section
export const AboutSection = () => {
  return (
    <section id="about" className="py-24 md:py-32 px-6 md:px-12" data-testid="about-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="grid md:grid-cols-2 gap-12 md:gap-20 items-center"
        >
          <motion.div variants={fadeInUp}>
            <p className="font-body text-sm tracking-widest text-sage uppercase mb-4">
              About the Workshop
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-charcoal leading-tight mb-8">
              A space of inquiry, not teaching
            </h2>
            <div className="space-y-6 text-muted-foreground font-body leading-relaxed">
              <p>
                The JDF Life Workshop is a space for deep self-inquiry into the fundamental causes of human suffering.
              </p>
              <p>
                It offers an opportunity to observe life as it is, without beliefs, methods, or borrowed answers, allowing clarity, freedom, and love to naturally flower.
              </p>
              <p className="text-charcoal font-medium">
                This is not therapy. This is not coaching. This is an invitation to see.
              </p>
            </div>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            className="relative"
          >
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_b8d44dd8-2c96-4ba7-b677-2badbe42776e/artifacts/d754qodt_About%20the%20Workshop%20.jpeg"
                alt="Workshop atmosphere"
                className="w-full h-full object-cover image-hover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-sand/30 rounded-full blur-2xl" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// What Makes This Unique Section
export const UniqueSection = () => {
  const features = [
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Lifefulness Meditation",
      description: "Not a technique, but meditation as living itself"
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "A New Way of Living",
      description: "Free from the psychological 'I'"
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: "The Art of Seeing",
      description: "Observing without judgment"
    },
    {
      icon: <Ear className="w-6 h-6" />,
      title: "The Art of Listening",
      description: "Understanding without bias"
    },
    {
      icon: <Flower2 className="w-6 h-6" />,
      title: "Creative Expression",
      description: "Dance, music, poetry, art"
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Gratitude Session",
      description: "Ending each day in awareness and thankfulness"
    }
  ];

  return (
    <section id="unique" className="py-24 md:py-32 px-6 md:px-12 bg-muted/30" data-testid="unique-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="text-center mb-16"
        >
          <motion.p variants={fadeInUp} className="font-body text-sm tracking-widest text-sage uppercase mb-4">
            What Makes This Unique
          </motion.p>
          <motion.h2 variants={fadeInUp} className="font-heading text-3xl md:text-4xl font-medium text-charcoal">
            This is not a conventional workshop
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={stagger}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="bg-white/50 backdrop-blur-sm border border-border/50 rounded-xl p-8 hover:border-sage/30 transition-colors duration-500"
              data-testid={`feature-card-${index}`}
            >
              <div className="w-12 h-12 rounded-full bg-sage/10 flex items-center justify-center text-sage mb-6">
                {feature.icon}
              </div>
              <h3 className="font-heading text-xl font-medium text-charcoal mb-3">
                {feature.title}
              </h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Who Is This For Section
export const ForWhomSection = () => {
  const questions = [
    "Can life be lived without conflict and suffering?",
    "Is freedom possible beyond conditioned beliefs?",
    "What is my purpose? Why do I feel disconnected or lonely?",
    "Can there be love, clarity, and peace in daily living?"
  ];

  return (
    <section className="py-24 md:py-32 px-6 md:px-12" data-testid="for-whom-section">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="text-center"
        >
          <motion.p variants={fadeInUp} className="font-body text-sm tracking-widest text-sage uppercase mb-4">
            Who Is This For
          </motion.p>
          <motion.h2 variants={fadeInUp} className="font-heading text-3xl md:text-4xl font-medium text-charcoal mb-12">
            For those deeply questioning life
          </motion.h2>

          <motion.div variants={stagger} className="space-y-4 mb-12">
            {questions.map((question, index) => (
              <motion.p
                key={index}
                variants={fadeInUp}
                className="font-heading text-lg md:text-xl italic text-muted-foreground"
              >
                {question}
              </motion.p>
            ))}
          </motion.div>

          <motion.p variants={fadeInUp} className="font-body text-base text-charcoal bg-sand/30 py-4 px-6 rounded-full inline-block">
            If these questions are alive in you, this space may be for you.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// Workshop Structure Section
export const StructureSection = () => {
  const details = [
    { icon: <Calendar className="w-5 h-5" />, label: "Duration", value: "3 Days" },
    { icon: <Clock className="w-5 h-5" />, label: "Sessions", value: "6 Sessions (Morning & Evening)" },
    { icon: <Users className="w-5 h-5" />, label: "Mode", value: "Primarily Online (Limited Offline)" },
    { icon: <Heart className="w-5 h-5" />, label: "Fees", value: "Free Gift to All" }
  ];

  return (
    <section id="structure" className="py-24 md:py-32 px-6 md:px-12 bg-muted/30" data-testid="structure-section">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <p className="font-body text-sm tracking-widest text-sage uppercase mb-4">
              Workshop Structure
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-charcoal">
              Simple. Clear. Focused.
            </h2>
          </motion.div>

          <motion.div variants={stagger} className="grid sm:grid-cols-2 gap-6">
            {details.map((detail, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="flex items-center gap-4 bg-white/70 rounded-xl p-6 border border-border/50"
                data-testid={`structure-detail-${index}`}
              >
                <div className="w-12 h-12 rounded-full bg-sage/10 flex items-center justify-center text-sage flex-shrink-0">
                  {detail.icon}
                </div>
                <div>
                  <p className="font-body text-sm text-muted-foreground">{detail.label}</p>
                  <p className="font-heading text-lg font-medium text-charcoal">{detail.value}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Fees Note */}
          <motion.div variants={fadeInUp} className="mt-8 text-center">
            <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              This workshop is offered as a free gift to all. Participants who feel moved may consider making a{" "}
              <a 
                href={DONATE_LINK} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sage hover:underline"
              >
                voluntary donation
              </a>{" "}
              to support JDF's work.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Video Section
export const VideoSection = () => {
  return (
    <section className="py-24 md:py-32 px-6 md:px-12" data-testid="video-section">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <p className="font-body text-sm tracking-widest text-sage uppercase mb-4">
              Workshop Invitation
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-charcoal">
              A Personal Invitation
            </h2>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            className="relative aspect-video rounded-2xl overflow-hidden shadow-ambient"
          >
            <iframe
              src={YOUTUBE_VIDEO}
              title="JDF Life Workshop Invitation"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
              data-testid="workshop-video"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// About JDF Section
export const AboutJDFSection = () => {
  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-muted/30" data-testid="about-jdf-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="grid md:grid-cols-2 gap-12 md:gap-20 items-center"
        >
          <motion.div variants={fadeInUp} className="order-2 md:order-1">
            <div className="aspect-square rounded-2xl overflow-hidden relative">
              <img
                src="https://customer-assets.emergentagent.com/job_b8d44dd8-2c96-4ba7-b677-2badbe42776e/artifacts/tnmq2otb_About%20JDF.png"
                alt="About JDF"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="order-1 md:order-2">
            <p className="font-body text-sm tracking-widest text-sage uppercase mb-4">
              About JDF
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-charcoal leading-tight mb-8">
              Jeevan Darshan Foundation
            </h2>
            <div className="space-y-6 text-muted-foreground font-body leading-relaxed">
              <p>
                Jeevan Darshan Foundation (JDF) is a non-profit organization founded and led by Bikram Jha to address the growing crisis in human consciousness.
              </p>
              <p>
                JDF aims to nurture a new way of living free from suffering, misery, conditioning, and inner slavery, rooted in Self-observation, love, peace, Inner Freedom, Clarity & Oneness.
              </p>
            </div>
            <a
              href="https://jdf.org.in/index"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-body text-sm text-sage hover:text-sage/80 transition-colors mt-6"
              data-testid="jdf-link"
            >
              Learn more about JDF
              <ExternalLink size={14} />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// About Facilitator Section
export const FacilitatorSection = () => {
  return (
    <section id="facilitator" className="py-24 md:py-32 px-6 md:px-12" data-testid="facilitator-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="grid md:grid-cols-2 gap-12 md:gap-20 items-center"
        >
          <motion.div variants={fadeInUp}>
            <p className="font-body text-sm tracking-widest text-sage uppercase mb-4">
              Facilitator
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-charcoal leading-tight mb-8">
              Bikram Jha
            </h2>
            <div className="space-y-5 text-muted-foreground font-body leading-relaxed text-sm md:text-base">
              <p>
                Bikram Jha is an explorer of truth who was born in a small village in Bihar. During his college years, he felt an emptiness in life, a deep sense of something missing. This led him to turn inward, seeking answers to life's questions.
              </p>
              <p>
                Through exploration of various philosophies, retreats, books, and dialogues, especially the teachings of J. Krishnamurti, he began to understand himself and gain insights that eventually helped him discover a new way of living free from suffering.
              </p>
              <p>
                His realization: the crises we see in the world, violence, division, and suffering, are mere reflections of the crisis within us. The root of disorder is the 'I', the illusion of separateness.
              </p>
              <p className="text-charcoal font-medium">
                Currently, he is spreading the message of love and a new way of living through various platforms, accompanying participants as a friend in inquiry and self-knowing.
              </p>
            </div>
            <a
              href="https://bikramjha.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-body text-sm text-sage hover:text-sage/80 transition-colors mt-6"
              data-testid="bikram-link"
            >
              More about Bikram Jha
              <ExternalLink size={14} />
            </a>
          </motion.div>

          <motion.div variants={fadeInUp} className="relative">
            <div className="rounded-2xl overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_b8d44dd8-2c96-4ba7-b677-2badbe42776e/artifacts/cs3xi6en_Untitled%20design%20%281%29.png"
                alt="Bikram Jha"
                className="w-full h-auto object-contain"
              />
            </div>
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-sage/20 rounded-full blur-2xl" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Email Subscription Section
export const SubscribeSection = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/subscribers`, { email, name: name || null });
      toast.success("Thank you for subscribing!");
      setEmail("");
      setName("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("This email is already subscribed");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-muted/30" data-testid="subscribe-section">
      <div className="max-w-xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="text-center"
        >
          <motion.div variants={fadeInUp}>
            <p className="font-body text-sm tracking-widest text-sage uppercase mb-4">
              Stay Connected
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-charcoal mb-4">
              Receive Updates
            </h2>
            <p className="font-body text-muted-foreground mb-8">
              Stay connected with JDF and receive updates on upcoming workshops and dialogues.
            </p>
          </motion.div>

          <motion.form
            variants={fadeInUp}
            onSubmit={handleSubscribe}
            className="space-y-4"
          >
            <Input
              type="text"
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-white/70 border-border/50 focus:border-sage"
              data-testid="subscribe-name-input"
            />
            <Input
              type="email"
              placeholder="Your email address *"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-white/70 border-border/50 focus:border-sage"
              data-testid="subscribe-email-input"
            />
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-sage hover:bg-sage/90 text-white rounded-full py-6"
              data-testid="subscribe-btn"
            >
              {loading ? "Subscribing..." : "Subscribe"}
            </Button>
          </motion.form>
        </motion.div>
      </div>
    </section>
  );
};

// Events Section
export const EventsSection = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events/active`);
        setEvents(response.data);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <section id="events" className="py-24 md:py-32 px-6 md:px-12" data-testid="events-section">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <p className="font-body text-sm tracking-widest text-sage uppercase mb-4">
              Upcoming Events
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-charcoal">
              Join the Next Workshop
            </h2>
            <p className="font-body text-muted-foreground mt-4">
              The workshop happens every month. Dates are announced below.
            </p>
          </motion.div>

          {loading ? (
            <div className="text-center text-muted-foreground">Loading events...</div>
          ) : events.length > 0 ? (
            <motion.div variants={stagger} className="space-y-6">
              {events.map((event, index) => (
                <motion.div
                  key={event.id}
                  variants={fadeInUp}
                  className="bg-white/70 rounded-xl p-6 md:p-8 border border-border/50 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                  data-testid={`event-card-${index}`}
                >
                  <div>
                    <h3 className="font-heading text-xl font-medium text-charcoal mb-2">
                      {event.title}
                    </h3>
                    <p className="font-body text-sm text-muted-foreground">
                      {formatDate(event.start_date)} to {formatDate(event.end_date)} • {event.mode}
                    </p>
                    {event.description && (
                      <p className="font-body text-sm text-muted-foreground mt-2">{event.description}</p>
                    )}
                  </div>
                  <a
                    href={event.registration_link || GOOGLE_FORM_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-body text-sm bg-sage text-white px-6 py-3 rounded-full hover:bg-sage/90 transition-all duration-300 text-center whitespace-nowrap"
                    data-testid={`event-register-btn-${index}`}
                  >
                    Register Now
                  </a>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div variants={fadeInUp} className="text-center">
              <div className="bg-sand/30 rounded-xl p-8 md:p-12">
                <p className="font-body text-muted-foreground mb-6">
                  New workshop dates will be announced soon. Register to stay updated.
                </p>
                <a
                  href={GOOGLE_FORM_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-body text-sm bg-sage text-white px-6 py-3 rounded-full hover:bg-sage/90 transition-all duration-300 inline-flex items-center gap-2"
                  data-testid="register-notify-btn"
                >
                  Register Interest
                  <ArrowRight size={16} />
                </a>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

// Closing Section
export const ClosingSection = () => {
  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-charcoal text-white" data-testid="closing-section">
      <div className="max-w-3xl mx-auto text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
        >
          <motion.p
            variants={fadeInUp}
            className="font-heading text-2xl md:text-3xl italic leading-relaxed mb-8 text-white/90"
          >
            This is not about becoming someone else.<br />
            It is about seeing what is, clearly, deeply, and together.
          </motion.p>
          
          <motion.div variants={fadeInUp}>
            <a
              href={GOOGLE_FORM_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="font-body bg-white text-charcoal px-8 py-4 rounded-full hover:bg-white/90 transition-all duration-500 hover:scale-105 inline-flex items-center gap-2"
              data-testid="closing-register-btn"
            >
              Register & Join the JDF Community
              <ArrowRight size={18} />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
export const Footer = () => {
  return (
    <footer className="py-12 px-6 md:px-12 bg-bone border-t border-border/50" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 md:gap-12">
          <div>
            <p className="font-heading text-xl font-medium text-charcoal mb-4">
              JDF Life Workshop
            </p>
            <p className="font-body text-sm text-muted-foreground leading-relaxed">
              Jeevan Darshan Foundation – Exploring life, as it is.
            </p>
          </div>

          <div>
            <p className="font-body text-sm font-medium text-charcoal mb-4 uppercase tracking-widest">
              Links
            </p>
            <div className="flex flex-col gap-3">
              <a
                href="https://jdf.org.in/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-body text-sm text-muted-foreground hover:text-sage transition-colors"
                data-testid="footer-jdf-link"
              >
                JDF Website
              </a>
              <a
                href="#events"
                className="font-body text-sm text-muted-foreground hover:text-sage transition-colors"
                data-testid="footer-events-link"
              >
                Events
              </a>
              <a
                href="https://bikramjha.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-body text-sm text-muted-foreground hover:text-sage transition-colors"
                data-testid="footer-bikram-link"
              >
                About Bikram Jha
              </a>
              <a
                href={DONATE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="font-body text-sm text-muted-foreground hover:text-sage transition-colors"
                data-testid="footer-donate-link"
              >
                Donate
              </a>
            </div>
          </div>

          <div>
            <p className="font-body text-sm font-medium text-charcoal mb-4 uppercase tracking-widest">
              Contact
            </p>
            <div className="flex flex-col gap-3">
              <a
                href="mailto:info@jdf.org.in"
                className="font-body text-sm text-muted-foreground hover:text-sage transition-colors inline-flex items-center gap-2"
                data-testid="footer-contact-email"
              >
                <Mail size={14} />
                info@jdf.org.in
              </a>
              <a
                href="tel:+919693647913"
                className="font-body text-sm text-muted-foreground hover:text-sage transition-colors inline-flex items-center gap-2"
                data-testid="footer-contact-phone"
              >
                <Phone size={14} />
                +91 9693647913
              </a>
            </div>
          </div>

          <div>
            <p className="font-body text-sm font-medium text-charcoal mb-4 uppercase tracking-widest">
              Support
            </p>
            <a
              href={DONATE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="font-body text-sm bg-sage/10 text-sage px-4 py-2 rounded-full hover:bg-sage/20 transition-colors inline-flex items-center gap-2"
              data-testid="footer-donate-btn"
            >
              <Heart size={14} />
              Make a Donation
            </a>
          </div>
        </div>

        <div className="section-divider my-8" />

        <p className="font-body text-xs text-muted-foreground text-center">
          © {new Date().getFullYear()} Jeevan Darshan Foundation. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

// Main Landing Page Component
export default function LandingPage() {
  return (
    <div className="bg-bone min-h-screen">
      <Toaster position="top-right" richColors />
      <Navigation />
      <HeroSection />
      <AboutSection />
      <UniqueSection />
      <ForWhomSection />
      <StructureSection />
      <VideoSection />
      <AboutJDFSection />
      <FacilitatorSection />
      <EventsSection />
      <SubscribeSection />
      <ClosingSection />
      <Footer />
    </div>
  );
}
